import { createContext, useContext, useEffect, useRef } from 'react'
import type { AnyAction, Middleware, Reducer } from 'redux'
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom'
import type {
  Action,
  Hash,
  History,
  Location,
  Search,
} from 'history'
import type { To } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'

export const LOCATION_CHANGE = '@@router/LOCATION_CHANGE'
export const CALL_HISTORY_METHOD = '@@router/CALL_HISTORY_METHOD'

const AppHistoryContext = createContext<History | undefined>(undefined)

export type RouterActionType = Action
export type LocationState = any
export type LocationWithState<S = LocationState> = Location & { state: S }
export type LocationDescriptorObject<S = LocationState> = Partial<LocationWithState<S>> & { pathname?: string }

export interface RouterLocation<S = LocationState> extends LocationWithState<S> {
  query: Record<string, string>
}

export interface RouterState<S = LocationState> {
  location: RouterLocation<S>
  action: RouterActionType
}

export interface LocationChangeAction<S = LocationState> {
  type: typeof LOCATION_CHANGE
  payload: RouterState<S>
}

export interface LocationActionPayload<A = Array<any>> {
  method: string
  args?: A
}

export interface CallHistoryMethodAction<A = Array<any>> {
  type: typeof CALL_HISTORY_METHOD
  payload: LocationActionPayload<A>
}

export type RouterAction<S = LocationState> = LocationChangeAction<S> | CallHistoryMethodAction

export function push<S = LocationState>(path: To, state?: S): CallHistoryMethodAction<[To, S?]>
export function push<S = LocationState>(location: LocationDescriptorObject<S>): CallHistoryMethodAction<[LocationDescriptorObject<S>]>
export function push(...args: Array<unknown>): CallHistoryMethodAction<Array<unknown>> {
  return updateLocation('push', args)
}

export function replace<S = LocationState>(path: To, state?: S): CallHistoryMethodAction<[To, S?]>
export function replace<S = LocationState>(location: LocationDescriptorObject<S>): CallHistoryMethodAction<[LocationDescriptorObject<S>]>
export function replace(...args: Array<unknown>): CallHistoryMethodAction<Array<unknown>> {
  return updateLocation('replace', args)
}

export function go(n: number): CallHistoryMethodAction<[number]> {
  return updateLocation('go', [n]) as CallHistoryMethodAction<[number]>
}

export function goBack(): CallHistoryMethodAction<[]> {
  return updateLocation('goBack', []) as CallHistoryMethodAction<[]>
}

export function goForward(): CallHistoryMethodAction<[]> {
  return updateLocation('goForward', []) as CallHistoryMethodAction<[]>
}

export const routerActions = {
  push,
  replace,
  go,
  goBack,
  goForward,
}

export function onLocationChanged<S = LocationState>(
  location: LocationWithState<S>,
  action: RouterActionType,
): LocationChangeAction<S> {
  return {
    type: LOCATION_CHANGE,
    payload: {
      location: injectQuery(location),
      action,
    },
  }
}

export function createRouterReducer<S = LocationState>(history: History): Reducer<RouterState<S>> {
  const initialRouterState = {
    location: injectQuery(history.location as LocationWithState<S>),
    action: history.action,
  }

  return (state = initialRouterState, action: AnyAction) => {
    if (action?.type !== LOCATION_CHANGE) return state

    return {
      location: injectQuery(action.payload.location),
      action: action.payload.action,
    }
  }
}

export function appRouterMiddleware(history: History): Middleware {
  return () => next => action => {
    const routerAction = action as CallHistoryMethodAction<Array<unknown>>

    if (routerAction.type !== CALL_HISTORY_METHOD) {
      return next(action)
    }

    const { method, args = [] } = routerAction.payload
    const historyMethod = getHistoryMethod(history, method)

    if (typeof historyMethod === 'function') {
      return historyMethod.apply(history, args)
    }
  }
}

interface AppHistoryRouterProps {
  history: History
  noInitialPop?: boolean
  children?: React.ReactNode
}

export function AppHistoryRouter({ history, noInitialPop = false, children }: AppHistoryRouterProps) {
  const dispatch = useAppDispatch()
  const routerLocation = useAppSelector(state => state.router?.location)
  const routerLocationRef = useRef(routerLocation ?? history.location as LocationWithState)

  useEffect(() => {
    routerLocationRef.current = routerLocation ?? history.location as LocationWithState
  }, [history, routerLocation])

  useEffect(() => {
    const unlisten = history.listen(({ location, action }) => {
      dispatch(onLocationChanged(location as LocationWithState, action) as any)
    })

    const shouldDispatchInitialLocation =
      history.action !== 'POP' ||
      (!noInitialPop && !isSamePathLocation(history.location as LocationWithState, routerLocationRef.current))

    if (shouldDispatchInitialLocation) {
      dispatch(onLocationChanged(history.location as LocationWithState, history.action) as any)
    }

    return unlisten
  }, [dispatch, history, noInitialPop])

  return <AppHistoryContext.Provider value={history}>
    <HistoryRouter history={history as any}>{children}</HistoryRouter>
  </AppHistoryContext.Provider>
}

export function useAppHistory(): History {
  const history = useContext(AppHistoryContext)

  if (!history) {
    throw new Error('useAppHistory must be used within AppHistoryRouter')
  }

  return history
}

export function useOptionalAppHistory(): History | undefined {
  return useContext(AppHistoryContext)
}

export function getRouter<S extends { router: RouterState }>(state: S): RouterState {
  return state.router
}

export function getAction<S extends { router: RouterState }>(state: S): RouterActionType {
  return state.router.action
}

export function getHash<S extends { router: RouterState }>(state: S): Hash {
  return state.router.location.hash
}

export function getLocation<S extends { router: RouterState }>(state: S): RouterLocation {
  return state.router.location
}

export function getSearch<S extends { router: RouterState }>(state: S): Search {
  return state.router.location.search
}

function updateLocation(method: string, args: Array<unknown>): CallHistoryMethodAction<Array<unknown>> {
  return {
    type: CALL_HISTORY_METHOD,
    payload: {
      method,
      args,
    },
  }
}

function getHistoryMethod(history: History, method: string): ((...args: Array<unknown>) => unknown) | undefined {
  if (method === 'goBack') return history.back.bind(history)
  if (method === 'goForward') return history.forward.bind(history)

  const historyMethod = history[method as keyof History]

  if (typeof historyMethod === 'function') {
    return (historyMethod as (...args: Array<unknown>) => unknown).bind(history)
  }
}

function injectQuery<S = LocationState>(location: LocationWithState<S>): RouterLocation<S> {
  if ('query' in location && location.query) {
    return location as RouterLocation<S>
  }

  const query = new URLSearchParams(location.search)

  return {
    ...location,
    query: Object.fromEntries(query.entries()),
  }
}

function isSamePathLocation(first: Pick<LocationWithState, 'pathname' | 'search' | 'hash'>, second: Pick<LocationWithState, 'pathname' | 'search' | 'hash'>): boolean {
  return first.pathname === second.pathname &&
    (first.search ?? '') === (second.search ?? '') &&
    (first.hash ?? '') === (second.hash ?? '')
}
